import React, { useState, useEffect } from 'react';
// Imports for common components
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API calls
import { getProducts, fetchProduct, destroyProduct, destroyProductsMultiple } from '../../../api/ProductsAPI';
// Utilities
import { handleApiError, getBaseUrl } from '../../../api/risapi'; // Added getBaseUrl
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const ProductsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Product');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);

    // --- State for Multi-select ---
    const [enableMultipleItemSelect, setEnableMultipleItemSelect] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());

    const [productsList, setProductsList] = useState({
        paginate: true,
        data: [],
        per_page: perPage,
        current_page: 1,
        tot_page: null,
    });
    const [productNumbering, setProductNumbering] = useState('auto');


    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/masters/products/create');
    const handleEditClick = (productId) => navigate(`/franchisee_user/masters/products/edit/${productId}`);

    // --- Delete Logic ---
    const handleDeleteClick = (productId) => {
        fetchProduct(productId, function (result) {
            if (result.product) {
                const deleteProduct = result.product;
                Swal.fire({
                    title: 'Are you sure?',
                    html: `Do you want to delete product <strong>${deleteProduct.name}</strong>?<br/>This action cannot be undone.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Yes, Delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyProduct(deleteProduct, function (apiResponse) {
                            if (apiResponse.product) {
                                Swal.fire('Deleted!', 'Record deleted.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch details.', 'error'); }
        });
    };

    // --- Multi-Delete Logic ---
    const showMultipleDeleteConfirmation = () => {
        const deletingProducts = productsList.data.filter(prod => selectedProductIds.has(prod.id));
        Swal.fire({
            title: `Delete ${deletingProducts.length} Products?`,
            html: `
                <p>You are about to delete the following products:</p>
                <div style="max-height: 150px; overflow-y: auto;">
                    ${deletingProducts.map(prod => `
                        <ul class="delete-details-list mb-2 text-start">
                            <li><strong>${prod.name}</strong> (${prod.code || 'No Code'})</li>
                        </ul>
                    `).join('')}
                </div>
                <p class="mt-2 text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete them!'
        }).then((result) => {
            if (result.isConfirmed) {
                const idsToDelete = Array.from(selectedProductIds);
                destroyProductsMultiple(idsToDelete, function (response) {
                    if (response.product) {
                        Swal.fire('Deleted!', 'Selected records deleted.', 'success');
                        setEnableMultipleItemSelect(false);
                        setSelectedProductIds(new Set());
                        setSaveCompleted(prev => !prev);
                    } else {
                        Swal.fire('Error!', response.error?.response?.data?.message || 'Could not delete.', 'error');
                    }
                });
            }
        });
    };

    // --- Checkbox Handler ---
    const handleCheckboxChange = (id, isChecked) => {
        setSelectedProductIds(prevSelectedIds => {
            const newSelectedIds = new Set(prevSelectedIds);
            if (isChecked) newSelectedIds.add(id);
            else newSelectedIds.delete(id);
            return newSelectedIds;
        });
    };

    // --- Effects ---
    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Products';
        let filters = {
            paginate: productsList.paginate,
            page: currentPage,
            per_page: perPage,
            search_term: searchTermCompleted,
        };

        setViewDataLoading(true);
        setSelectedProductIds(new Set());
        setEnableMultipleItemSelect(false);

        getProducts(filters, function (response) {
            setViewDataLoading(false);
            if (response.products) {
                 let productsData = [];
                 let currentPageNum = 1;
                 let lastPageNum = 1;
                 let responsePerPage = perPage;

                 if (Array.isArray(response.products)) {
                     productsData = response.products;
                 } else if (response.products.data && Array.isArray(response.products.data)) {
                     productsData = response.products.data;
                     currentPageNum = response.products.current_page;
                     lastPageNum = response.products.last_page;
                     responsePerPage = response.products.per_page;
                 } else {
                     handleApiError(response);
                     return;
                 }

                setProductsList({
                    paginate: filters.paginate,
                    per_page: responsePerPage,
                    data: productsData,
                    current_page: currentPageNum,
                    tot_page: lastPageNum,
                });
                setProductNumbering(response.product_numbering || 'auto');
            } else {
                handleApiError(response);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load products.' });
                setShowToaster(true);
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);


    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Products List'>
                        <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Products List</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Products</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Product
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {productsList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={productsList.current_page} totalPages={productsList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            {/* --- Multi-select buttons --- */}
                                            <div className='d-flex align-items-center ms-auto mt-2 mt-md-0'>
                                                <button className="btn btn-info btn-sm me-2" type="button" onClick={() => setEnableMultipleItemSelect(prev => !prev)}>
                                                    {enableMultipleItemSelect ? (<><i className="fa-solid fa-check fa-fw me-1" />Done</>) : 'Select'}
                                                </button>
                                                {enableMultipleItemSelect && selectedProductIds.size > 0 && (
                                                    <button className="btn btn-danger btn-sm" type="button" onClick={showMultipleDeleteConfirmation}>
                                                        Delete ({selectedProductIds.size}) Records
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No products found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && productsList.data.map((product) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={product.id}>
                                                <div className="card hover-effect">
                                                    {/* Merged Content Area */}
                                                    <div className="card-body p-2">
                                                        <div className="row g-0 align-items-center h-100">
                                                            
                                                            {/* --- Image Section (25% Width) --- */}
                                                            <div className="col-3 text-center">
                                                                <div 
                                                                    className="rounded overflow-hidden bg-light d-flex align-items-center justify-content-center border"
                                                                    style={{ width: '100%', aspectRatio: '1/1', position: 'relative' }}
                                                                >
                                                                    {product.image_path ? (
                                                                        <img 
                                                                            src={`${getBaseUrl()}/storage/${product.image_path}`} 
                                                                            alt={product.name} 
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                        />
                                                                    ) : (
                                                                        <i className="ti ti-package text-secondary" style={{ fontSize: '2rem' }}></i> 
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* --- Details Section (75% Width) --- */}
                                                            <div className="col-9 ps-3">
                                                                <div className="d-flex justify-content-between">
                                                                    {/* Left side: Checkbox + Name */}
                                                                    <div className="d-flex align-items-start overflow-hidden">
                                                                        {enableMultipleItemSelect && (
                                                                            <div className="me-2 mt-1">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    checked={selectedProductIds.has(product.id)}
                                                                                    onChange={(e) => handleCheckboxChange(product.id, e.target.checked)}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        <div>
                                                                            <h6 className="mb-1 text-dark f-w-600 text-truncate" title={product.name}>
                                                                                {product.name}
                                                                            </h6>
                                                                            {product.code && <div className="text-muted f-s-12 mb-1">Code: {product.code}</div>}
                                                                            {product.baseUom && <span className="badge bg-light text-dark border fw-normal">Unit: {product.baseUom.name}</span>}
                                                                        </div>
                                                                    </div>

                                                                    {/* Right side: Actions Dropdown */}
                                                                    <div className="dropdown ms-1">
                                                                        <button aria-expanded="false" className="bg-none border-0 p-1" data-bs-toggle="dropdown" type="button">
                                                                            <i className="ti ti-dots-vertical text-dark f-s-16" />
                                                                        </button>
                                                                        <ul className="dropdown-menu dropdown-menu-end">
                                                                            <li><a className="dropdown-item" onClick={() => handleEditClick(product.id)}><i className="ti ti-edit text-success me-1" /> Edit</a></li>
                                                                            <li><a className="dropdown-item delete-button" onClick={() => handleDeleteClick(product.id)}><i className="ti ti-trash text-danger me-1" /> Delete</a></li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {/* --- No Data Card --- */}
                                        {!viewDataLoading && productsList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Products Found</h5><p className="card-text">No records found matching your criteria or no products added yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default ProductsPage;